import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiMenuAlt4, HiPencil, HiPlus, HiRefresh, HiTrash } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import "./PaymentGatewayIndex.scss";

interface PaymentGateway {
  id: number;
  name: string;
  localizedName?: string | null;
  currencyId?: number;
  currencyName?: string | null;
  currencySymbol?: string | null;
  isActive: boolean;
  type: number;
  displayOrder: number;
  createdAt: string;
  updatedAt?: string;
}

const GATEWAY_TYPE_MAP: Record<number, { labelKey: string; color: string }> = {
  0: { labelKey: "paymentGateways:typeOptions.disabled", color: "secondary" },
  1: { labelKey: "paymentGateways:typeOptions.enabledWhatsapp", color: "success" },
  2: { labelKey: "paymentGateways:typeOptions.enabledOnlinePayment", color: "primary" },
};

const sortByDisplayOrder = (items: PaymentGateway[]) =>
  [...items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.id - b.id);

export const PaymentGatewayIndex: React.FC = () => {
  const { t, i18n } = useTranslation(["paymentGateways", "common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const canCreate = hasPermission("Create Payment Gateway");
  const canUpdate = hasPermission("Update Payment Gateway");
  const canDelete = hasPermission("Delete Payment Gateway");

  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [orderChanged, setOrderChanged] = useState(false);

  const fetchPaymentGateways = async () => {
    try {
      setLoading(true);
      const response = await adminApi.call("/paymentgateway?page=1&pageSize=1000", "GET");

      if (!response.success) {
        showErrorToast(response.error || t("paymentGateways:messages.fetchError"));
        return;
      }

      setPaymentGateways(sortByDisplayOrder(response.data?.items || []));
    } catch (error) {
      showErrorToast(t("paymentGateways:messages.fetchError"));
      console.error("Error fetching payment gateways:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentGateways();
  }, []);

  const filteredGateways = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) {
      return paymentGateways;
    }

    return paymentGateways.filter((gateway) =>
      [gateway.localizedName, gateway.name, gateway.currencySymbol, gateway.currencyName]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedSearchTerm))
    );
  }, [paymentGateways, searchTerm]);

  const persistOrder = async (orderedGateways: PaymentGateway[]) => {
    try {
      setReordering(true);
      const response = await adminApi.call("/paymentgateway/reorder", "PUT", {
        gatewayIds: orderedGateways.map((gateway) => gateway.id),
      });

      if (!response.success) {
        showErrorToast(response.error || t("paymentGateways:messages.reorderError"));
        await fetchPaymentGateways();
        return;
      }

      setPaymentGateways(
        orderedGateways.map((gateway, index) => ({
          ...gateway,
          displayOrder: index + 1,
        }))
      );
      showSuccessToast(t("paymentGateways:messages.reorderSuccess"));
    } catch (error) {
      showErrorToast(t("paymentGateways:messages.reorderError"));
      console.error("Error reordering payment gateways:", error);
      await fetchPaymentGateways();
    } finally {
      setReordering(false);
      setOrderChanged(false);
    }
  };

  const handleDragStart = (index: number) => {
    if (!canUpdate || reordering) return;
    setDraggedItemIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>, index: number) => {
    event.preventDefault();

    if (!canUpdate || draggedItemIndex === null || draggedItemIndex === index) {
      return;
    }

    setPaymentGateways((current) => {
      const reordered = [...current];
      const draggedItem = reordered[draggedItemIndex];
      reordered.splice(draggedItemIndex, 1);
      reordered.splice(index, 0, draggedItem);
      return reordered;
    });

    setDraggedItemIndex(index);
    setOrderChanged(true);
  };

  const handleDragEnd = async () => {
    setDraggedItemIndex(null);

    if (!orderChanged) {
      return;
    }

    await persistOrder(paymentGateways);
  };

  const deletePaymentGateway = async (id: number) => {
    if (!window.confirm(t("paymentGateways:messages.deleteConfirm"))) {
      return;
    }

    try {
      const response = await adminApi.call(`/paymentgateway/${id}`, "DELETE");

      if (!response.success) {
        showErrorToast(response.error || t("paymentGateways:messages.deleteError"));
        return;
      }

      showSuccessToast(t("paymentGateways:messages.deleteSuccess"));
      fetchPaymentGateways();
    } catch (error) {
      showErrorToast(t("paymentGateways:messages.deleteError"));
      console.error("Error deleting payment gateway:", error);
    }
  };

  const breadcrumbs = [{ label: t("paymentGateways:breadcrumbs.paymentGateways") }];

  const headerActions = (
    <div className="payment-gateway-header-actions">
      <ActionButton variant="secondary" icon={HiRefresh} onClick={fetchPaymentGateways} disabled={loading || reordering}>
        {t("common:refresh")}
      </ActionButton>
      {canCreate && (
        <ActionButton variant="primary" icon={HiPlus} onClick={() => navigate("/payment-gateways/create")}>
          {t("paymentGateways:addNew")}
        </ActionButton>
      )}
    </div>
  );

  return (
    <PageLayout
      title={t("paymentGateways:title")}
      subtitle={t("paymentGateways:subtitle")}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      className={`payment-gateway-index ${isRTL ? "rtl" : "ltr"}`}
      isIndexPage={true}
    >
      <div className="card">
        <div className="card-body">
          <div className="payment-gateway-toolbar">
            <div>
              <h5>{t("paymentGateways:table.reorder", "Display Order")}</h5>
              <p>{t("paymentGateways:messages.dragToReorder")}</p>
            </div>
            <input
              type="text"
              className="form-control payment-gateway-search"
              placeholder={t("common:search")}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="table-responsive">
            <table className="table align-middle payment-gateway-table">
              <thead>
                <tr>
                  <th className="drag-column"></th>
                  <th>{t("paymentGateways:table.displayOrder")}</th>
                  <th>{t("paymentGateways:table.name")}</th>
                  <th>{t("paymentGateways:table.currencyCode")}</th>
                  <th>{t("paymentGateways:table.type")}</th>
                  <th>{t("paymentGateways:table.isActive")}</th>
                  <th>{t("paymentGateways:table.createdAt")}</th>
                  <th className="text-center">{t("paymentGateways:table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredGateways.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5 text-muted">
                      {t("paymentGateways:messages.noPaymentGateways")}
                    </td>
                  </tr>
                ) : (
                  filteredGateways.map((paymentGateway, index) => {
                    const typeInfo = GATEWAY_TYPE_MAP[paymentGateway.type] || GATEWAY_TYPE_MAP[0];

                    return (
                      <tr
                        key={paymentGateway.id}
                        draggable={canUpdate && !searchTerm.trim()}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(event) => handleDragOver(event, index)}
                        onDragEnd={handleDragEnd}
                        className={draggedItemIndex === index ? "dragging" : ""}
                      >
                        <td className="drag-column">
                          {canUpdate && !searchTerm.trim() ? <HiMenuAlt4 className="drag-handle" /> : null}
                        </td>
                        <td>{paymentGateway.displayOrder}</td>
                        <td>{paymentGateway.localizedName || paymentGateway.name}</td>
                        <td>
                          {paymentGateway.currencySymbol
                            ? `${paymentGateway.currencySymbol}${paymentGateway.currencyName ? ` - ${paymentGateway.currencyName}` : ""}`
                            : "-"}
                        </td>
                        <td>
                          <span className={`badge bg-${typeInfo.color}`}>{t(typeInfo.labelKey)}</span>
                        </td>
                        <td>
                          <span className={`badge bg-${paymentGateway.isActive ? "success" : "danger"}`}>
                            {paymentGateway.isActive ? t("common:active") : t("common:inactive")}
                          </span>
                        </td>
                        <td>
                          {paymentGateway.createdAt
                            ? new Date(paymentGateway.createdAt).toLocaleString("en-GB", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "-"}
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            {canUpdate && (
                              <ActionButton
                                variant="warning"
                                size="sm"
                                icon={HiPencil}
                                onClick={() => navigate(`/payment-gateways/edit/${paymentGateway.id}`)}
                                tooltip={t("paymentGateways:editPaymentGateway")}
                              />
                            )}
                            {canDelete && (
                              <ActionButton
                                variant="danger"
                                size="sm"
                                icon={HiTrash}
                                onClick={() => deletePaymentGateway(paymentGateway.id)}
                                tooltip={t("paymentGateways:deletePaymentGateway")}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {searchTerm.trim() && (
            <div className="payment-gateway-note">
              {t("paymentGateways:messages.reorderSearchDisabled")}
            </div>
          )}

          {reordering && (
            <div className="payment-gateway-note">{t("paymentGateways:messages.reordering")}</div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
